import type { SupabaseClient } from '@supabase/supabase-js'

export type OrderType = 'pickup' | 'delivery' | 'dinein'
export type OrderStatus = 'new' | 'accepted' | 'preparing' | 'ready' | 'completed' | 'cancelled'

export interface OrderItem {
  id: string          // menu_item id (or random)
  name: string
  price: number       // unit price
  quantity: number
  notes?: string
}

export interface Order {
  id: string
  restaurant_id: string
  order_number: number
  type: OrderType
  status: OrderStatus
  customer_name: string
  customer_phone: string | null
  customer_email: string | null
  address: string | null
  items: OrderItem[]
  subtotal: number
  delivery_fee: number
  total: number
  payment_method: string | null
  notes: string | null
  scheduled_for: string | null
  eta_minutes: number | null
  cancellation_reason: string | null
  driver_name: string | null
  driver_phone: string | null
  driver_assigned_at: string | null
  /** Set for dine-in ("Vor Ort") orders placed via a table QR code. */
  table_number: string | null
  created_at: string
  updated_at?: string
}

export async function assignDriver(
  supabase: SupabaseClient,
  orderId: string,
  driverName: string,
  driverPhone: string | null,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('orders').update({
    driver_name: driverName,
    driver_phone: driverPhone,
    driver_assigned_at: new Date().toISOString(),
  }).eq('id', orderId)
  return { error: error?.message ?? null }
}

export interface NewOrder {
  restaurant_id: string
  type: OrderType
  customer_name: string
  customer_phone?: string
  customer_email?: string
  address?: string
  items: OrderItem[]
  subtotal: number
  delivery_fee?: number
  total: number
  payment_method?: string
  notes?: string
  scheduled_for?: string
  table_number?: string
}

export async function listOrders(
  supabase: SupabaseClient,
  restaurantId: string,
  opts?: { dateISO?: string; status?: OrderStatus[] },
): Promise<Order[]> {
  let q = supabase
    .from('orders')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: false })

  if (opts?.dateISO) {
    const start = new Date(opts.dateISO + 'T00:00:00').toISOString()
    const end = new Date(opts.dateISO + 'T23:59:59.999').toISOString()
    q = q.gte('created_at', start).lte('created_at', end)
  }
  if (opts?.status && opts.status.length > 0) {
    q = q.in('status', opts.status)
  }

  const { data, error } = await q
  if (error) {
    console.error('listOrders', error)
    return []
  }
  return (data ?? []) as Order[]
}

export async function createOrder(
  supabase: SupabaseClient,
  input: NewOrder,
): Promise<{ data: Order | null; error: string | null }> {
  // No .select() / .single() — same reason as createBooking: the public
  // menu page submits as anon, which has INSERT permission but no SELECT
  // on orders. Adding RETURNING would surface as a "row violates RLS"
  // even though the INSERT itself succeeded.
  const { error } = await supabase
    .from('orders')
    .insert({
      restaurant_id: input.restaurant_id,
      type: input.type,
      customer_name: input.customer_name,
      customer_phone: input.customer_phone ?? null,
      customer_email: input.customer_email ?? null,
      address: input.address ?? null,
      items: input.items,
      subtotal: input.subtotal,
      delivery_fee: input.delivery_fee ?? 0,
      total: input.total,
      payment_method: input.payment_method ?? null,
      notes: input.notes ?? null,
      scheduled_for: input.scheduled_for ?? null,
      table_number: input.table_number ?? null,
    })
  if (error) return { data: null, error: error.message }
  return { data: null, error: null }
}

export async function updateOrderStatus(
  supabase: SupabaseClient,
  id: string,
  status: OrderStatus,
  extra: { cancellation_reason?: string; eta_minutes?: number } = {},
): Promise<{ error: string | null }> {
  const patch: Record<string, unknown> = { status }
  if (extra.cancellation_reason !== undefined) patch.cancellation_reason = extra.cancellation_reason
  if (extra.eta_minutes !== undefined) patch.eta_minutes = extra.eta_minutes
  const { error } = await supabase.from('orders').update(patch).eq('id', id)
  return { error: error?.message ?? null }
}

export async function deleteOrder(
  supabase: SupabaseClient,
  id: string,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('orders').delete().eq('id', id)
  return { error: error?.message ?? null }
}

export const STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'Neu',
  accepted: 'Angenommen',
  preparing: 'In Zubereitung',
  ready: 'Bereit',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
}

export const STATUS_COLORS: Record<OrderStatus, string> = {
  new: 'bg-amber-100 text-amber-700',
  accepted: 'bg-blue-100 text-blue-700',
  preparing: 'bg-brand-100 text-brand-700',
  ready: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-neutral-200 text-neutral-700',
  cancelled: 'bg-red-100 text-red-700',
}

export const TYPE_LABELS: Record<OrderType, string> = {
  pickup: 'Abholung',
  delivery: 'Lieferung',
  dinein: 'Vor Ort',
}
